import React, { ReactElement } from "react";

import Card from "@mui/material/Card";
import CardHeader from "@mui/material/CardHeader";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import { useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";

import IconSelector from "components/shared/images/IconSelector";
import GenericChip from "components/shared/status/GenericChip";

import { IServerDetails } from "types/api/serversApiInterface";

import InstallNfsClient from "../actions/InstallNfsClient";
import RemoveNodeFromCluster from "../actions/RemoveNodeFromCluster";
import UninstallNfsClient from "../actions/UninstallNfsClient";

const ClusterServer = ({
 serverData,
 nfsInstalled
}: {
 serverData: IServerDetails;
 nfsInstalled: boolean;
}): ReactElement => {
 const theme = useTheme();
 const desktopViewPort = useMediaQuery(theme.breakpoints.up("lg"));

 const formatStatus = (status: string) => {
  switch (status) {
   case "running":
    return "success";
   case "initializing":
    return "info";
   case "starting":
    return "warning";
   case "stopping":
    return "warning";
   case "off":
    return "error";
   case "deleting":
    return "error";
   case "migrating":
    return "info";
   case "rebuilding":
    return "warning";
   default:
    return "default";
  }
 };

 const glusterText = () => {
  switch (serverData?.cluster?.glusterFsInstalled) {
   case 0:
    return "not installed";
   case 1:
    return "installed - not initialized";
   case 2:
    return "running";
   default:
    return "not installed";
  }
 };

 const glusterColor = () => {
  switch (serverData?.cluster?.glusterFsInstalled) {
   case 0:
    return "error";
   case 1:
    return "warning";
   case 2:
    return "success";
   default:
    return "error";
  }
 };

 return (
  <Card
   sx={{
    minWidth: desktopViewPort ? 400 : "auto"
   }}>
   <CardHeader
    avatar={
     <IconSelector icon="DockerIcon" props={{ sx: { width: 50, height: 50 }, color: "kxOrange" }} />
    }
    title={
     <Stack direction="row" spacing={1} alignItems="center">
      <Chip
       color={formatStatus(serverData.server_status)}
       size="small"
       sx={{ width: 16, height: 16 }}
      />
      <Typography textTransform="uppercase" fontWeight="bold">
       {serverData.server_name}
      </Typography>
      {serverData.nfs_installed && <GenericChip text="NFS" type="success" />}
     </Stack>
    }
    subheader={
     <Stack>
      <Typography variant="extraSmall">{`Server ${serverData.server_type_name} - ${
       serverData.ipv4_ip
      } - ${serverData.private_net.map((element) => {
       return element.ip;
      })}`}</Typography>
      <Stack direction="row" spacing={0.5}>
       <Typography textAlign="center" variant="extraSmall">
        {"Volume FS - "}
       </Typography>
       <Stack direction="row" spacing={1} alignItems="center">
        <Typography fontWeight="bold" variant="extraSmall" color={glusterColor()}>
         {glusterText()}
        </Typography>
       </Stack>
      </Stack>
      {nfsInstalled && !serverData.nfs_installed && (
       <Stack direction="row" spacing={0.5}>
        <Typography textAlign="center" variant="extraSmall">
         {"NFS client - "}
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
         <Typography
          fontWeight="bold"
          variant="extraSmall"
          color={serverData.nfs_client ? "success" : "error"}>
          {serverData.nfs_client ? "running" : "not installed"}
         </Typography>
        </Stack>
       </Stack>
      )}
     </Stack>
    }
    sx={{ textAlign: "left" }}
   />
   <Stack pb={1}>
    <Divider sx={{ borderColor: "#EF711A", mx: 1 }} />
    <Stack direction="row" spacing={1} px={1} pt={1}>
     <RemoveNodeFromCluster id={serverData.id} />
     {nfsInstalled &&
      !serverData.nfs_installed &&
      (serverData.nfs_client ? (
       <UninstallNfsClient id={serverData.id} />
      ) : (
       <InstallNfsClient id={serverData.id} />
      ))}
    </Stack>
   </Stack>
  </Card>
 );
};

export default ClusterServer;
